import {
  Form,
  Row,
  Col,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { businessMembershipSchema } from "../schemas/Schema";
import { countries } from "countries-list";
import { BusinessMembershipFormData } from "../../types/UserData";
import { useNavigate } from "react-router-dom";
import SelectInput from "../common/SelectInput";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import { useQueryClient } from "@tanstack/react-query";
import { ImageUrls } from "../../types/Types";
import { useAppContext } from "../../Context/AppContext";
import { toast } from "react-toastify";
import { useGetUser } from "../../api/queries/useGetUser";
import { useState, useMemo, useCallback, memo } from "react";
import ImageCropperModal from "../common/ImageCropperModal";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { splitCityCountry } from "../../utils/cityAsyncSelectOptions";
import { BUSINESS_TURNOVER_OPTIONS } from "../../utils/businessTurnover";
import { SORTED_SECTORS } from "../../utils/sectors";
import { resolveProfessionalPhotoUrl } from "../../utils/profilePhoto";

const renderTooltip = (msg: string) => <Tooltip id="tooltip">{msg}</Tooltip>;

const SectionHeader = memo(function SectionHeader({
  title,
  tooltip,
}: {
  title: string;
  tooltip: string;
}) {
  return (
    <div className="d-flex align-items-center text-left mb-3 gap-2">
      <h5 className="mb-1">{title}</h5>
      <OverlayTrigger placement="right" overlay={renderTooltip(tooltip)}>
        <img width="15px" src="/info.png" alt="info" />
      </OverlayTrigger>
    </div>
  );
});
SectionHeader.displayName = "SectionHeader";

const currencies = ["AED", "EUR", "GBP", "IDR", "MYR", "SAR", "SGD", "USD"];

const genders = ["Male", "Female"];

const languageOptions = [
  { value: "English", label: "English" },
  { value: "Arabic", label: "Arabic" },
  { value: "Turkish", label: "Turkish" },
  { value: "Bahasa Indonesia", label: "Bahasa Indonesia" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Malay", label: "Malay" },
  { value: "Albanian", label: "Albanian" },
  { value: "Bosnian", label: "Bosnian" },
  { value: "Urdu", label: "Urdu" },
];

const products = [
  {
    value: "Attending Networking Events",
    label: "Attending Networking Events",
  },
  {
    value: "Expanding into International Markets",
    label: "Expanding into International Markets",
  },
  { value: "Investment", label: "Investment" },
  { value: "Mentorship", label: "Mentorship" },
  {
    value: "Sharia advice for your business",
    label: "Sharia advice for your business",
  },
  { value: "Starting a new business", label: "Starting a new business" },
];

interface BusinessMembershipFormProps {
  defaultData?: BusinessMembershipFormData & { country?: string | null };
  showEditModal?: boolean;
  setShowEditModal?: (show: boolean) => void;
  defaultImages?: ImageUrls;
  completionPercent?: number;
}

const EditBusinessData = memo(function EditBusinessData({
  defaultData,
  showEditModal,
  setShowEditModal,
  defaultImages,
  completionPercent,
}: BusinessMembershipFormProps) {
  const { t, i18n } = useTranslation();
  const { city: defaultCity, country: defaultBusinessCountry } = useMemo(
    () =>
      splitCityCountry(
        defaultData?.city,
        defaultData?.businessCountry || defaultData?.country,
      ),
    [defaultData?.city, defaultData?.businessCountry, defaultData?.country],
  );
  const uiText = useMemo(
    () => ({
      productsGoal: "What’s your next business goal?",
      selectProducts: "Select Products...",
      businessIdentificationTitle: "Business Identification Information",
      businessName: "Business Name*",
      logo: "Logo",
      replaceLogo: "Replace Logo",
      deleteLogo: "Delete Logo",
      sectorIndustry: "Sector / Industry*",
      selectSector: "Select Sector/Industry...",
      websiteUrl: "Website URL",
      websitePlaceholder: "https://www.example.com",
      countriesOperation: "Countries of Operation",
      selectCountries: "Select all Countries that apply",
      aboutBusiness: "About the Business",
      businessContactTitle: "Business Contact Information",
      firstAddress: "First Line of Address*",
      secondAddress: "Second Line of Address",
      county: "County",
      city: "City*",
      postcode: "Postcode*",
      country: "Country",
      selectCountry: "Select Country",
      businessPhone: "Business Telephone Number*",
      userPersonalTitle: "User Personal Information",
      gender: "Gender*",
      selectGender: "Select Gender...",
      languagePreference: "Language Preference",
      selectLanguage: "Select Language",
      aboutYou: "About You",
      professionalPhoto: "Professional Photo",
      positionCompany: "Position in Company*",
      financialTitle: "Business Financial and Strategic Information",
      chooseCurrency: "Choose Currency*",
      selectCurrency: "Select Currency...",
      turnover: "Annual Business Turnover (GBP)*",
      selectTurnover: "Select turnover (GBP)...",
      businessObjectives: "Business Objectives*",
      platformTitle: "Platform-Specific Information",
      productsServices: "Your Products or Services Offered",
      discountsMembers: "Discounts for SMBF Members",
      yourDiscountCode: "Your Discount Code",
      save: "Save",
      saving: "Saving...",
      tipBusinessIdentification:
        'We ask for your business name, logo, and industry to showcase your brand and help others recognise your business on our platform. The "About the Business" section and website let you share your story and expertise, while listing countries of operation helps connect you with the right partners or clients in your regions of focus.',
      tipBusinessContact:
        "Providing your business address helps us verify your company and makes it easier for others to find and connect with you, especially for local or regional opportunities. This information ensures we can match you with relevant contacts based on your location.",
      tipUserPersonal:
        'Sharing your gender and language preferences allows us to personalise your experience and communication on the platform. Your "About You" section and professional photo make your profile engaging and approachable, while your position clarifies your role, helping others understand who they’re connecting with.',
      tipFinancial:
        "Your business turnover helps us understand your company’s scale so we can recommend the best opportunities for you. Sharing your objectives (e.g., finding partners, clients, or funding) lets us tailor connections and suggestions to match your goals.",
      tipPlatform:
        "Sharing the products or services your business offers helps us promote your expertise to other members, making it easier for potential partners or clients to find you. Listing any discounts you’re willing to offer platform members can attract new connections and encourage collaboration.",
      male: "Male",
      female: "Female",
    }),
    []
  );
  const { data: translatedUiText } = useTranslateDynamicFields(
    uiText,
    i18n.resolvedLanguage || i18n.language
  );
  const sectorUiText = useMemo(
    () => Object.fromEntries(SORTED_SECTORS.map((s, i) => [`sector_${i}`, s])),
    []
  );
  const { data: translatedSectors } = useTranslateDynamicFields(
    sectorUiText,
    i18n.resolvedLanguage || i18n.language
  );
  const tt = (key: keyof typeof uiText) => translatedUiText?.[key] || uiText[key];
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    setError,
    clearErrors,
    watch,
  } = useForm({
    resolver: yupResolver(businessMembershipSchema),
    defaultValues: {
      ...defaultData,
      city: defaultCity,
      businessCountry: defaultBusinessCountry,
      businessAddressLineTwo: defaultData?.businessAddressLineTwo || undefined,
      professionalPhoto:
        resolveProfessionalPhotoUrl(defaultImages) ||
        "",
      businessLogo: defaultImages?.BusinessLogo || "",
    },
  });
  const [showCropper, setShowCropper] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [croppedPhotoFile, setCroppedPhotoFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const currentLogo = watch("businessLogo");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, objectId } = useAppContext();
  const isAdmin = currentUser?.user?.isAdmin === 1;
  const { mutate: updateUser, isPending } = useSaveUser({
    onSuccess: async (updatedUserData) => {
      await queryClient.invalidateQueries({ queryKey: ["user", objectId] });
      if (showEditModal) {
        setShowEditModal?.(false);
      }
      if (!defaultData) {
        navigate("/my-account");
      }
    },
    onError: () => {
      toast.error(t("feedback.userRegisterFailed"));
    },
  });
  const { data: userData } = useGetUser({ id: objectId || "" });

  const handleCloseCropper = useCallback(() => setShowCropper(false), []);

  const countryOptions = useMemo(
    () =>
      Object.entries(countries)
        .map(([code, country]) => ({
          value: country.name,
          label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  const onSubmit = useCallback(
    (data: BusinessMembershipFormData) => {
      if (errors.professionalPhoto || errors.businessLogo) {
        if (errors.professionalPhoto) {
          setError("professionalPhoto", {
            type: "manual",
            message: errors.professionalPhoto.message,
          });
        }
        if (errors.businessLogo) {
          setError("businessLogo", {
            type: "manual",
            message: errors.businessLogo.message,
          });
        }

        return;
      }

      // Create FormData object
      const formData = new FormData();
    // Append file fields
    if (logoFile) {
      formData.append("businessLogo", logoFile);
    } else {
      // Fallback to file input if logoFile is not set
      const logo = (document.getElementById("businessLogo") as HTMLInputElement)
        ?.files?.[0];
      if (logo) {
        formData.append("businessLogo", logo);
      }
    }

      const mergedData = {
        ...userData?.user,
        ...data,
        phoneNumber: data.phoneNumber ?? undefined,
        businessAddressLineTwo: data.businessAddressLineTwo ?? undefined,
        hasCompletedProfile: 1,
      };

      // Append all non-file fields to FormData
      Object.entries(mergedData).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          key !== "businessLogo" &&
          key !== "professionalPhoto" &&
          key !== "personalProfessionalPhoto"
        ) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // Append file fields
      const logo = (document.getElementById("businessLogo") as HTMLInputElement)
        ?.files?.[0];
      if (logo) {
        formData.append("businessLogo", logo);
      }

      if (croppedPhotoFile) {
        formData.append("professionalPhoto", croppedPhotoFile);
      }

      // Store merged data in localStorage (without files)
      localStorage.setItem("UserProfile", JSON.stringify(mergedData));

      // Send FormData to API
      updateUser(formData);
    },
    [userData, croppedPhotoFile, errors, logoFile, setError, updateUser]
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="mb-4 shadow-sm rounded-4 py-3">
        <Col md={12}>
          <Form.Group controlId="productInterested" className="mt-3">
            <SelectInput
              name="productInterested"
              label={tt("productsGoal")}
              placeholder={tt("selectProducts")}
              control={control}
              options={products}
              error={errors.productInterested}
              isRequired
              isMulti
            />
          </Form.Group>
        </Col>

      </Row>
      <Row className="mb-4 shadow-sm rounded-4 py-3">
        <Col md={12}>
          <SectionHeader
            title={tt("businessIdentificationTitle")}
            tooltip={tt("tipBusinessIdentification")}
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessName">
            <Form.Label>{tt("businessName")}</Form.Label>
            <Form.Control
              disabled={showEditModal && !isAdmin}
              type="text"
              {...register("businessName")}
              isInvalid={!!errors.businessName}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessName?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessLogo">
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip(
                "PNG, JPG, JPEG, HEIF, HEIC, PDF, Min 200x200px, Max 5MB"
              )}
            >
              <Form.Label className="d-block mb-2">{tt("logo")}</Form.Label>
            </OverlayTrigger>
            {currentLogo ? (
              <>
                <Form.Control
                  id="businessLogo"
                  type="file"
                  accept=".png,.jpg,.jpeg,.heif,.heic,image/png,image/jpeg,image/heif,image/heic"
                  className="d-none"
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (file) {
                      const maxSize = 5 * 1024 * 1024;
                      const validTypes = [
                        "image/png",
                        "image/jpeg",
                        "image/heif",
                        "image/heic",
                      ];

                      if (!validTypes.includes(file.type)) {
                        setError("businessLogo", {
                          type: "manual",
                          message: "Unsupported File Format. Please upload an image (PNG, JPG, HEIF, or HEIC).",
                        });
                        (e.target as HTMLInputElement).value = "";
                        return;
                      }

                      if (file.size > maxSize) {
                        setError("businessLogo", {
                          type: "manual",
                          message: "File size must be less than 5MB",
                        });
                        (e.target as HTMLInputElement).value = "";
                        return;
                      }

                      // Store the file for form submission
                      setLogoFile(file);

                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const base64String = reader.result?.toString();
                        if (!base64String) {
                          setError("businessLogo", {
                            type: "manual",
                            message: "File upload failed. Please try again.",
                          });
                          return;
                        }

                        const img = new Image();
                        img.onload = () => {
                          if (img.width < 200 || img.height < 200) {
                            setError("businessLogo", {
                              type: "manual",
                              message: "Image must be at least 200x200 pixels",
                            });
                            setLogoFile(null);
                            return;
                          }

                          clearErrors("businessLogo");
                          const event = {
                            target: {
                              name: "businessLogo",
                              value: base64String,
                            },
                          };
                          register("businessLogo").onChange(event);
                        };
                        img.onerror = () => {
                          setError("businessLogo", {
                            type: "manual",
                            message: "File upload failed. Please try again.",
                          });
                          setLogoFile(null);
                        };
                        img.src = base64String;
                      };

                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <div
                  className="position-relative d-inline-block logo-preview-wrapper"
                  onMouseEnter={(e) => {
                    const overlay = e.currentTarget.querySelector(".logo-overlay") as HTMLElement;
                    const container = e.currentTarget.querySelector(".logo-container") as HTMLElement;
                    if (overlay) overlay.classList.add("show");
                    if (container) {
                      container.classList.add("logo-hover");
                    }
                  }}
                  onMouseLeave={(e) => {
                    const overlay = e.currentTarget.querySelector(".logo-overlay") as HTMLElement;
                    const container = e.currentTarget.querySelector(".logo-container") as HTMLElement;
                    if (overlay) overlay.classList.remove("show");
                    if (container) {
                      container.classList.remove("logo-hover");
                    }
                  }}
                  onTouchStart={(e) => {
                    const overlay = e.currentTarget.querySelector(".logo-overlay") as HTMLElement;
                    const container = e.currentTarget.querySelector(".logo-container") as HTMLElement;
                    if (overlay) overlay.classList.add("show");
                    if (container) {
                      container.classList.add("logo-hover");
                    }
                  }}
                >
                  <div className="logo-container w-100 h-100 border rounded p-2 p-md-3 bg-white d-flex align-items-center justify-content-center overflow-hidden">
                    <img
                      src={currentLogo}
                      alt="Business Logo"
                      className="img-fluid"
                    />
                  </div>
                  <div className="logo-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center gap-2 gap-md-3 rounded">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const fileInput = document.getElementById("businessLogo") as HTMLInputElement;
                        if (fileInput) {
                          fileInput.click();
                        }
                      }}
                      className="logo-action-btn logo-action-btn-replace border-0 bg-white rounded-circle d-flex align-items-center justify-content-center"
                      title={tt("replaceLogo")}
                    >
                      <FaEdit size={16} className="d-md-none" color="#0d6efd" />
                      <FaEdit size={18} className="d-none d-md-block" color="#0d6efd" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setValue("businessLogo", "");
                        setLogoFile(null);
                        const fileInput = document.getElementById("businessLogo") as HTMLInputElement;
                        if (fileInput) {
                          fileInput.value = "";
                        }
                      }}
                      className="logo-action-btn logo-action-btn-delete border-0 bg-white rounded-circle d-flex align-items-center justify-content-center"
                      title={tt("deleteLogo")}
                    >
                      <FaTrash size={16} className="d-md-none" color="#dc3545" />
                      <FaTrash size={18} className="d-none d-md-block" color="#dc3545" />
                    </button>
                  </div>
                </div>
                <style>{`
                  .logo-preview-wrapper {
                    width: 120px;
                    height: 80px;
                  }
                  .logo-container {
                    transition: border-color 0.3s ease, box-shadow 0.3s ease;
                  }
                  .logo-container.logo-hover {
                    border-color: #0d6efd !important;
                    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
                  }
                  .logo-overlay {
                    background-color: rgba(0, 0, 0, 0.75);
                    opacity: 0;
                    transition: opacity 0.3s ease-in-out;
                    pointer-events: none;
                  }
                  .logo-overlay.show {
                    opacity: 1;
                    pointer-events: auto;
                  }
                  .logo-action-btn {
                    width: 42px;
                    height: 42px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    cursor: pointer;
                    transition: all 0.2s ease;
                    pointer-events: auto;
                  }
                  .logo-action-btn:hover {
                    transform: scale(1.15);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.4);
                  }
                  .logo-action-btn:active {
                    transform: scale(1.1);
                  }
                  .logo-action-btn-replace:hover {
                    background-color: #f8f9fa !important;
                  }
                  .logo-action-btn-delete:hover {
                    background-color: #fff5f5 !important;
                  }
                  @media (max-width: 767.98px) {
                    .logo-preview-wrapper {
                      width: 100px;
                      height: 70px;
                    }
                    .logo-action-btn {
                      width: 36px;
                      height: 36px;
                    }
                    .logo-overlay.show {
                      opacity: 1;
                      pointer-events: auto;
                    }
                  }
                  @media (max-width: 575.98px) {
                    .logo-preview-wrapper {
                      width: 90px;
                      height: 60px;
                    }
                    .logo-action-btn {
                      width: 32px;
                      height: 32px;
                    }
                  }
                `}</style>
              </>
            ) : (
              <Form.Control
                id="businessLogo"
                type="file"
                accept=".png,.jpg,.jpeg,.heif,.heic,image/png,image/jpeg,image/heif,image/heic"
                onChange={(e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const maxSize = 5 * 1024 * 1024;
                    const validTypes = [
                      "image/png",
                      "image/jpeg",
                      "image/heif",
                      "image/heic",
                    ];

                    if (!validTypes.includes(file.type)) {
                      setError("businessLogo", {
                        type: "manual",
                        message: "Unsupported File Format. Please upload an image (PNG, JPG, HEIF, or HEIC).",
                      });
                      (e.target as HTMLInputElement).value = "";
                      return;
                    }

                    if (file.size > maxSize) {
                      setError("businessLogo", {
                        type: "manual",
                        message: "File size must be less than 5MB",
                      });
                      (e.target as HTMLInputElement).value = "";
                      return;
                    }

                    // Store the file for form submission
                    setLogoFile(file);

                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result?.toString();
                      if (!base64String) {
                        setError("businessLogo", {
                          type: "manual",
                          message: "File upload failed. Please try again.",
                        });
                        return;
                      }

                      const img = new Image();
                      img.onload = () => {
                        if (img.width < 200 || img.height < 200) {
                          setError("businessLogo", {
                            type: "manual",
                            message: "Image must be at least 200x200 pixels",
                          });
                          setLogoFile(null);
                          return;
                        }

                        clearErrors("businessLogo");
                        const event = {
                          target: {
                            name: "businessLogo",
                            value: base64String,
                          },
                        };
                        register("businessLogo").onChange(event);
                      };
                      img.onerror = () => {
                        setError("businessLogo", {
                          type: "manual",
                          message: "File upload failed. Please try again.",
                        });
                        setLogoFile(null);
                      };
                      img.src = base64String;
                    };

                    reader.readAsDataURL(file);
                  }
                }}
                isInvalid={!!errors.businessLogo}
              />
            )}
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessLogo?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="sector" className="mt-3">
            <Form.Label>{tt("sectorIndustry")}</Form.Label>
            <Form.Select
              {...register("businessSectorIndustry")}
              isInvalid={!!errors.businessSectorIndustry}
              defaultValue={defaultData?.businessSectorIndustry || ""}
            >
              <option value="" disabled hidden>
                {tt("selectSector")}
              </option>
              {SORTED_SECTORS.map((sector, idx) => (
                <option key={idx} value={sector}>
                  {translatedSectors?.[`sector_${idx}`] || sector}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessSectorIndustry?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessWebsiteUrl" className="mt-3">
            <Form.Label>{tt("websiteUrl")}</Form.Label>
            <Form.Control
              type="text"
              placeholder={tt("websitePlaceholder")}
              {...register("businessWebsiteUrl", {
                onBlur: (e) => {
                  let v = e.target.value.trim();
                  if (!v) return;
                  if (!/^https?:\/\//i.test(v)) v = "https://" + v;
                  try {
                    const u = new URL(v);
                    if (!u.hostname.startsWith("www."))
                      u.hostname = "www." + u.hostname;
                    setValue("businessWebsiteUrl", u.toString(), {
                      shouldValidate: true,
                    });
                  } catch {
                    setValue("businessWebsiteUrl", v, { shouldValidate: true });
                  }
                },
              })}
              isInvalid={!!errors.businessWebsiteUrl}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessWebsiteUrl?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessCountryOperation" className="mt-3">
            <SelectInput
              disabled={showEditModal && !isAdmin}
              name="businessCountryOperation"
              label={tt("countriesOperation")}
              placeholder={tt("selectCountries")}
              control={control}
              options={countryOptions}
              error={errors.businessCountryOperation}
              isRequired
              isMulti
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="businessAbout" className="mt-3">
            <Form.Label>{tt("aboutBusiness")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("businessAbout")}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4 shadow-sm rounded-4 py-3">
        <Col md={12}>
          <SectionHeader
            title={tt("businessContactTitle")}
            tooltip={tt("tipBusinessContact")}
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessAddressLineOne">
            <Form.Label>{tt("firstAddress")}</Form.Label>
            <Form.Control
              type="text"
              {...register("businessAddressLineOne")}
              isInvalid={!!errors.businessAddressLineOne}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessAddressLineOne?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessAddressLineTwo">
            <Form.Label>{tt("secondAddress")}</Form.Label>
            <Form.Control type="text" {...register("businessAddressLineTwo")} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="county" className="mt-3">
            <Form.Label>{tt("county")}</Form.Label>
            <Form.Control type="text" {...register("county")} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="city" className="mt-3">
            <Form.Label>{tt("city")}</Form.Label>
            <Form.Control
              disabled={showEditModal && !isAdmin}
              type="text"
              {...register("city")}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.city?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="businessPostCode" className="mt-3">
            <Form.Label>{tt("postcode")}</Form.Label>
            <Form.Control
              type="text"
              {...register("businessPostCode")}
              isInvalid={!!errors.businessPostCode}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessPostCode?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessCountry" className="mt-3">
            <SelectInput
              disabled={showEditModal && !isAdmin}
              name="businessCountry"
              label={tt("country")}
              placeholder={tt("selectCountry")}
              control={control}
              options={countryOptions}
              error={errors.businessCountry}
              isRequired
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessTelephoneNumber" className="mt-3">
            <Form.Label>{tt("businessPhone")}</Form.Label>
            <Form.Control
              disabled={showEditModal && !isAdmin}
              type="text"
              maxLength={15}
              {...register("businessTelephoneNumber")}
              isInvalid={!!errors.businessTelephoneNumber}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessTelephoneNumber?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4 shadow-sm rounded-4 py-3">
        <Col md={12}>
          <SectionHeader
            title={tt("userPersonalTitle")}
            tooltip={tt("tipUserPersonal")}
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="personalGender">
            <Form.Label>{tt("gender")}</Form.Label>
            <Form.Select
              {...register("personalGender")}
              isInvalid={!!errors.personalGender}
              defaultValue={defaultData?.personalGender || ""}
            >
              <option value="" disabled hidden>
                {tt("selectGender")}
              </option>
              {genders.map((gender, idx) => (
                <option key={idx} value={gender}>
                  {gender === "Male" ? tt("male") : tt("female")}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.personalGender?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="personalLanguagePreference">
            <SelectInput
              name="personalLanguagePreference"
              label={tt("languagePreference")}
              placeholder={tt("selectLanguage")}
              control={control}
              options={languageOptions}
              error={errors.personalLanguagePreference}
              isRequired
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="personalAbout" className="mt-3">
            <Form.Label>{tt("aboutYou")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("personalAbout")}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="professionalPhoto" className="mt-3">
            <OverlayTrigger
              placement="right"
              overlay={renderTooltip(
                "PNG, JPG, JPEG, HEIF, HEIC, PDF, Min 200x200px, Max 5MB"
              )}
            >
              <Form.Label>{tt("professionalPhoto")}</Form.Label>
            </OverlayTrigger>
            <Form.Control
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;

                const maxSize = 5 * 1024 * 1024;
                const validTypes = ["image/png", "image/jpeg"];

                if (!validTypes.includes(file.type)) {
                  setError("professionalPhoto", {
                    type: "manual",
                    message: "Unsupported File Format",
                  });
                  return;
                }

                if (file.size > maxSize) {
                  setError("professionalPhoto", {
                    type: "manual",
                    message: "File must be less than 5MB",
                  });
                  return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64 = reader.result as string;
                  setTempImage(base64);
                  setShowCropper(true);
                };
                reader.readAsDataURL(file);
              }}
              isInvalid={!!errors.professionalPhoto}
            />

            {errors.professionalPhoto?.message && (
              <Form.Control.Feedback
                // className="text-danger px-1" style={{ fontSize: 10 }}
                type="invalid"
              >
                {errors.professionalPhoto?.message}
              </Form.Control.Feedback>
            )}

            {/* Cropper Modal */}
            {tempImage && (
              <ImageCropperModal
                show={showCropper}
                imageSrc={tempImage}
                aspectRatio={1} // 1:1
                onClose={handleCloseCropper}
                onSave={(croppedFile, base64) => {
                  const img = new Image();
                  img.onload = () => {
                    if (img.width < 200 || img.height < 200) {
                      setError("professionalPhoto", {
                        type: "manual",
                        message: "Cropped image must be at least 200x200px",
                      });
                      return;
                    }

                    clearErrors("professionalPhoto");

                    // Save cropped file to state
                    setCroppedPhotoFile(croppedFile);

                    // Optionally store base64 if needed for preview
                    setShowCropper(false);
                    setTempImage(null);
                  };
                  img.src = base64;
                }}
              />
            )}
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.professionalPhoto?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="personalPositionCompany" className="mt-3">
            <Form.Label>{tt("positionCompany")}</Form.Label>
            <Form.Control
              type="text"
              {...register("personalPositionCompany")}
              isInvalid={!!errors.personalPositionCompany}
            />
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.personalPositionCompany?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4 shadow-sm rounded-4 py-3">
        <Col md={12}>
          <SectionHeader
            title={tt("financialTitle")}
            tooltip={tt("tipFinancial")}
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessCurrency">
            <Form.Label>{tt("chooseCurrency")}</Form.Label>
            <Form.Select
              {...register("businessCurrency")}
              isInvalid={!!errors.businessCurrency}
              defaultValue={defaultData?.businessCurrency || ""}
            >
              <option value="" disabled hidden>
                {tt("selectCurrency")}
              </option>
              {currencies.map((currency, idx) => (
                <option key={idx} value={currency}>
                  {currency}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessCurrency?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessTurnover">
            <Form.Label>{tt("turnover")}</Form.Label>
            <Form.Select
              {...register("businessTurnover")}
              isInvalid={!!errors.businessTurnover}
              defaultValue={defaultData?.businessTurnover || ""}
            >
              <option value="" disabled hidden>
                {tt("selectTurnover")}
              </option>
              {BUSINESS_TURNOVER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback
              // className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessTurnover?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="businessObjectives" className="mt-3">
            <Form.Label>{tt("businessObjectives")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register("businessObjectives")}
              isInvalid={!!errors.businessObjectives}
            />
            <Form.Control.Feedback
              className="text-danger px-1" style={{ fontSize: 10 }}
              type="invalid"
            >
              {errors.businessObjectives?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-4 shadow-sm rounded-4 py-3">
        <Col md={12}>
          <SectionHeader
            title={tt("platformTitle")}
            tooltip={tt("tipPlatform")}
          />
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessProductServices">
            <Form.Label>{tt("productsServices")}</Form.Label>
            <Form.Control
              type="text"
              {...register("businessProductServices")}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessDiscount">
            <Form.Label>{tt("discountsMembers")}</Form.Label>
            <Form.Control type="text" {...register("businessDiscount")} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="businessDiscountCode" className="mt-3">
            <Form.Label>{tt("yourDiscountCode")}</Form.Label>
            <Form.Control type="text" {...register("businessDiscountCode")} />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end">
        <Button
          disabled={isPending}
          variant="primary"
          type="submit"
          className="px-4 py-2"
        >
          {isPending ? tt("saving") : tt("save")}
        </Button>
      </div>
    </Form>
  );
});

EditBusinessData.displayName = "EditBusinessData";

export default EditBusinessData;
